import IApiProvider from '../../../infrastructure/providers/abstract/IApiProvider';
import MerpsApiProvider from '../../../infrastructure/providers/pb-adapter/MerpsApiProvider';
import { downloadFileFromBlob } from '../../../services/common/files';
import { AbstractPrintService } from '../../../services/print/AbstractPrintService';
import PrintService from '../../../services/print/PrintService';
import { getNotifications } from '../../selectors/notifications';
import {
  getExpiredSystemNotificationIds,
  hasSystemNotifications,
} from '../../selectors/systemNotifications';
import error from '../../slices/error';
import notifications, {
  ErrorNotification,
  NotificationIcons,
  NotificationsViewModel,
  PrintNotification,
  SingleNotification,
} from '../../slices/notifications';
import systemNotifications from '../../slices/systemNotifications';
import { apiCall, createAppListener, postDispatch } from '../listenerHelpers';

const notificationsListener = createAppListener();

const apiProvider: IApiProvider = new MerpsApiProvider();
const printService: AbstractPrintService = new PrintService();

const createErrorNotification = ({
  id,
  payload: { label, message },
  title,
}: Record<string, any>): ErrorNotification => ({
  id,
  label: label ?? title,
  message: message ?? title,
  kind: 'error',
  icon: NotificationIcons.ERROR,
});

const createPrintNotification = ({
  id,
  payload: { queueId, status, label },
}: Record<string, any>): PrintNotification => ({
  queueId,
  id,
  status,
  label,
  kind: 'print',
  icon: NotificationIcons.PRINT,
});

const createNotification = (base: Record<string, any>): SingleNotification => {
  if (base.kind === 'print') {
    return createPrintNotification(base);
  }
  if (base.kind === 'error') {
    return createErrorNotification(base);
  }
  throw new Error('Unsupported notification type' + base.kind);
};

const handlePrintNotificationClicked = async ({
  queueId,
  label,
  id,
}: PrintNotification) => {
  const {
    pdf: { id: pdfStreamId },
  } = (await printService.getPrintStream(queueId)) as any;

  const buffer = await apiProvider.streams.read(pdfStreamId);

  downloadFileFromBlob(label, buffer);

  return { id };
};

notificationsListener.startListening(
  apiCall(
    {
      request: notifications.click,
      success: notifications.clickSuccess,
      failure: notifications.clickFailure,
    },
    async (payload, state, dispatch) => {
      const notification = getNotifications(state)[payload];

      void apiProvider.notifications
        .deleteNotification(notification.id)
        .catch(() => {});

      if (notification.kind === 'print') {
        return handlePrintNotificationClicked(notification);
      } else if (notification.kind === 'error') {
        dispatch(
          error.showNotification({
            name: 'RemoteError',
            id: 'systemError',
            code: null,
            message: notification.message || notification.label,
            stack: null,
            info: notification.message || notification.label,
            logout: false,
          })
        );
      }

      return { id: notification.id };
    }
  )
);

notificationsListener.startListening(
  apiCall(
    {
      request: notifications.get,
      success: notifications.getSuccess,
      failure: notifications.getFailure,
    },
    async () => {
      const serverNotifications =
        await apiProvider.notifications.getUserNotifications();
      const notificationsMap: NotificationsViewModel = {};

      for (const sNotification of Object.values(serverNotifications.user)) {
        const notification = createNotification(sNotification);
        notificationsMap[notification.id] = notification;
      }

      return { notifications: notificationsMap };
    }
  )
);

notificationsListener.startListening(
  apiCall(
    {
      request: notifications.delete,
      success: notifications.deleteSuccess,
      failure: notifications.deleteFailure,
    },
    async payload => {
      await apiProvider.notifications.deleteNotification(payload.id);
      return payload;
    }
  )
);

notificationsListener.startListening(
  postDispatch(notifications.subscribe, (_payload, state, dispatch) => {
    apiProvider.notifications.addListenerOnNewNotification(payload => {
      if (payload.interfaceName === 'notifications') {
        if (payload.eventName === 'system') {
          dispatch(systemNotifications.received(payload.notification));
        } else if (payload.eventName === 'system-delete') {
          dispatch(systemNotifications.delete(payload.notificationId));
        } else if (payload.eventName === 'system-update') {
          if (hasSystemNotifications(state)) {
            const expiredNotificationsIds = getExpiredSystemNotificationIds(
              state,
              new Date()
            );
            dispatch(systemNotifications.update(expiredNotificationsIds));
          }
        }
      } else {
        dispatch(notifications.received(payload));
      }
    });
  })
);

notificationsListener.startListening(
  postDispatch(notifications.received, (payload, _state, dispatch) => {
    dispatch(notifications.append(createNotification(payload)));
  })
);

export default notificationsListener.middleware;
